import * as XLSX from 'xlsx'
import type {
  FinanceiroComissaoRow,
  FinanceiroComissaoRootListing,
  FinanceiroComissaoSubpasta,
  FinanceiroComissaoSubpastaInfo,
} from '../types/financeiro'

const ELIGIBLE_EXT = /\.(xlsx|xls|csv)$/i

export function isEligibleComissaoFile(file: { name: string }): boolean {
  const name = file.name
  if (name.startsWith('~$')) return false
  return ELIGIBLE_EXT.test(name)
}

export function getRootFolderNameFromFiles(files: FileList | File[]): string | null {
  const first = Array.from(files)[0] as (File & { webkitRelativePath?: string }) | undefined
  if (!first) return null
  const relative = first.webkitRelativePath || first.name
  const parts = relative.split(/[/\\]/).filter(Boolean)
  if (parts.length <= 1) return null
  return parts[0] || null
}

/**
 * Extrai o ano da primeira parte do nome da pasta.
 * Ex.: "2024_Janeiro", "2024-01", "2024 Janeiro", "202401" → "2024"
 */
export function extractYearFromFolderName(name: string): string | null {
  const trimmed = name.trim()
  if (!trimmed) return null
  const firstPart = trimmed.split(/[_\-\s./\\]+/)[0] || trimmed
  if (/^\d{4}$/.test(firstPart)) return firstPart
  const leading = /^(\d{4})/.exec(firstPart)
  return leading ? leading[1] : null
}

function buildYearsAndSort(subfolders: FinanceiroComissaoSubpastaInfo[]): {
  years: string[]
  subfolders: FinanceiroComissaoSubpastaInfo[]
} {
  const yearSet = new Set<string>()
  for (const s of subfolders) {
    if (s.year) yearSet.add(s.year)
  }
  const years = Array.from(yearSet).sort((a, b) => b.localeCompare(a))
  const sorted = [...subfolders].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  return { years, subfolders: sorted }
}

export function isShowDirectoryPickerAvailable(): boolean {
  return typeof window !== 'undefined' && typeof (window as Window & { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function'
}

type AnyDirHandle = {
  kind: 'directory'
  name: string
  entries: () => AsyncIterableIterator<[string, AnyFsHandle]>
}

type AnyFileHandle = {
  kind: 'file'
  name: string
  getFile: () => Promise<File>
}

type AnyFsHandle = AnyDirHandle | AnyFileHandle

/** Coleta arquivos elegíveis recursivamente apenas dentro da pasta escolhida. */
async function collectEligibleFilesRecursive(dir: AnyDirHandle): Promise<File[]> {
  const out: File[] = []
  for await (const [, handle] of dir.entries()) {
    if (handle.kind === 'file') {
      if (isEligibleComissaoFile({ name: handle.name })) {
        try {
          out.push(await handle.getFile())
        } catch {
          /* ignore */
        }
      }
    } else if (handle.kind === 'directory') {
      out.push(...(await collectEligibleFilesRecursive(handle)))
    }
  }
  return out
}

export type FinanceiroRootPickerSession = FinanceiroComissaoRootListing & {
  /** Carrega Excel/CSV só das subpastas informadas (após filtrar por ano). */
  loadSubfolders: (names: string[]) => Promise<FinanceiroComissaoSubpasta[]>
}

/**
 * Abre o seletor da pasta raiz e lista só os nomes das subpastas (rápido).
 * Não deve ser usado para escolher uma subpasta isolada — a validação na UI exige subpastas com ano.
 */
export async function pickComissaoRootFolder(): Promise<FinanceiroRootPickerSession | null> {
  if (!isShowDirectoryPickerAvailable()) return null

  const showDirectoryPicker = (
    window as unknown as {
      showDirectoryPicker: (opts?: { mode?: string }) => Promise<AnyDirHandle>
    }
  ).showDirectoryPicker

  let root: AnyDirHandle
  try {
    root = await showDirectoryPicker({ mode: 'read' })
  } catch (err) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
      return null
    }
    throw err
  }

  const handleByName = new Map<string, AnyDirHandle>()
  const infos: FinanceiroComissaoSubpastaInfo[] = []

  for await (const [name, handle] of root.entries()) {
    if (handle.kind !== 'directory') continue
    handleByName.set(name, handle)
    infos.push({ name, year: extractYearFromFolderName(name) })
  }

  const { years, subfolders } = buildYearsAndSort(infos)

  return {
    rootName: root.name,
    years,
    subfolders,
    loadSubfolders: async (names: string[]) => {
      const result: FinanceiroComissaoSubpasta[] = []
      for (const name of names) {
        const handle = handleByName.get(name)
        if (!handle) continue
        const files = await collectEligibleFilesRecursive(handle)
        result.push({ name, files, fileCount: files.length })
      }
      return result.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    },
  }
}

/**
 * Fallback webkitdirectory: monta listagem só pelos caminhos (sem ler conteúdo).
 */
export function listingFromWebkitFiles(files: FileList | File[]): FinanceiroRootPickerSession | null {
  const list = Array.from(files)
  if (list.length === 0) return null

  const rootName = getRootFolderNameFromFiles(list) || 'Pasta selecionada'
  const filesBySubfolder = new Map<string, File[]>()

  for (const file of list) {
    const relative = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
    const parts = relative.split(/[/\\]/).filter(Boolean)
    // root / subpasta / ...arquivo  OU  root / arquivo
    if (parts.length < 2) continue
    if (parts.length === 2) {
      // arquivo na raiz — ignora para o filtro por ano de subpastas
      continue
    }
    const subName = parts[1]
    if (!isEligibleComissaoFile(file)) {
      if (!filesBySubfolder.has(subName)) filesBySubfolder.set(subName, [])
      continue
    }
    const existing = filesBySubfolder.get(subName) ?? []
    existing.push(file)
    filesBySubfolder.set(subName, existing)
  }

  const infos: FinanceiroComissaoSubpastaInfo[] = Array.from(filesBySubfolder.keys()).map((name) => ({
    name,
    year: extractYearFromFolderName(name),
  }))

  if (infos.length === 0) return null

  const { years, subfolders } = buildYearsAndSort(infos)

  return {
    rootName,
    years,
    subfolders,
    loadSubfolders: async (names: string[]) => {
      return names
        .filter((n) => filesBySubfolder.has(n))
        .map((name) => {
          const folderFiles = filesBySubfolder.get(name) ?? []
          return { name, files: folderFiles, fileCount: folderFiles.length }
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    },
  }
}

type FieldKey =
  | 'codigoCorretor'
  | 'nomeCorretor'
  | 'cpfCnpj'
  | 'apolice'
  | 'numeroSub'
  | 'modulo'
  | 'estipulante'
  | 'subEstipulante'
  | 'fatura'
  | 'competencia'
  | 'valorApropriado'
  | 'tipoComissao'
  | 'parcelaInicial'
  | 'numeroGrupoCobertura'
  | 'valorBase'
  | 'percentualComissao'
  | 'valorComissao'
  | 'dataEmissao'
  | 'dataApropriacao'
  | 'valorPremioBruto'
  | 'valorPremioTarifario'
  | 'dataMovimento'
  | 'dataAgendamento'
  | 'dataPagamento'
  | 'dataPagamentoEfetivo'
  | 'statusRecibo'
  | 'nrCobranca'

const HEADER_SYNONYMS: Record<string, FieldKey> = {
  'codigo do corretor': 'codigoCorretor',
  'cod. corretor': 'codigoCorretor',
  'cod corretor': 'codigoCorretor',
  'nome do corretor': 'nomeCorretor',
  'cpf/cnpj': 'cpfCnpj',
  'cpf cnpj': 'cpfCnpj',
  cpfcnpj: 'cpfCnpj',
  apolice: 'apolice',
  'numero sub': 'numeroSub',
  'nº sub': 'numeroSub',
  'nr sub': 'numeroSub',
  modulo: 'modulo',
  estipulante: 'estipulante',
  'sub estipulante': 'subEstipulante',
  fatura: 'fatura',
  competencia: 'competencia',
  'valor apropriado': 'valorApropriado',
  'tipo de comissao': 'tipoComissao',
  'tipo comissao': 'tipoComissao',
  'parcela inicial': 'parcelaInicial',
  'numero do grupo de cobertura': 'numeroGrupoCobertura',
  'nr grupo cobertura': 'numeroGrupoCobertura',
  'valor base': 'valorBase',
  'percentual de comissao': 'percentualComissao',
  'percentual comissao': 'percentualComissao',
  '% comissao': 'percentualComissao',
  'valor da comissao': 'valorComissao',
  'valor comissao': 'valorComissao',
  'data de emissao': 'dataEmissao',
  'data emissao': 'dataEmissao',
  'data da apropriacao': 'dataApropriacao',
  'data apropriacao': 'dataApropriacao',
  'valor do premio bruto': 'valorPremioBruto',
  'valor premio bruto': 'valorPremioBruto',
  'valor do premio tarifario': 'valorPremioTarifario',
  'valor premio tarifario': 'valorPremioTarifario',
  'data do movimento': 'dataMovimento',
  'data movimento': 'dataMovimento',
  'data do agendamento': 'dataAgendamento',
  'data agendamento': 'dataAgendamento',
  'data do pagamento': 'dataPagamento',
  'data pagamento': 'dataPagamento',
  'data do pagamento efetivo': 'dataPagamentoEfetivo',
  'data pagamento efetivo': 'dataPagamentoEfetivo',
  status_recibo: 'statusRecibo',
  'status recibo': 'statusRecibo',
  nr_cobranca: 'nrCobranca',
  'nr cobranca': 'nrCobranca',
  'nº cobranca': 'nrCobranca',
}

function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '')
}

function normalizeUnicodeSpaces(s: string): string {
  return s
    .replace(/\u00A0|\u1680|\u2000|\u2001|\u2002|\u2003|\u2004|\u2005|\u2006|\u2007|\u2008|\u2009|\u200A|\u202F|\u205F|\u3000/g, ' ')
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '')
}

function normalizeHeaderKey(raw: unknown): string {
  let s = String(raw ?? '').replace(/^\uFEFF/, '')
  s = normalizeUnicodeSpaces(s)
    .trim()
    .replace(/^[\s"'“”‘’`]+|[\s"'“”‘’`]+$/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\s+/g, ' ')
  try {
    s = s.normalize('NFKC')
  } catch {
    /* ignore */
  }
  s = stripDiacritics(s).toLowerCase()
  s = s.replace(/\s*[*#:•]+\s*$/, '').replace(/\s*\([^)]*\)\s*$/, '').trim()
  return s
}

function mapHeaderToField(normalized: string): FieldKey | null {
  if (normalized in HEADER_SYNONYMS) return HEADER_SYNONYMS[normalized]
  if (normalized.includes('codigo') && normalized.includes('corretor')) return 'codigoCorretor'
  if (normalized.includes('nome') && normalized.includes('corretor')) return 'nomeCorretor'
  if (normalized.includes('cpf') || normalized.includes('cnpj')) return 'cpfCnpj'
  if (normalized.includes('apolice')) return 'apolice'
  if (normalized.includes('numero sub') || normalized === 'numero sub') return 'numeroSub'
  if (normalized === 'modulo') return 'modulo'
  if (normalized === 'estipulante') return 'estipulante'
  if (normalized.includes('sub') && normalized.includes('estipulante')) return 'subEstipulante'
  if (normalized === 'fatura') return 'fatura'
  if (normalized.includes('competencia')) return 'competencia'
  if (normalized.includes('valor apropriado')) return 'valorApropriado'
  if (normalized.includes('tipo') && normalized.includes('comissao')) return 'tipoComissao'
  if (normalized.includes('parcela inicial')) return 'parcelaInicial'
  if (normalized.includes('grupo') && normalized.includes('cobertura')) return 'numeroGrupoCobertura'
  if (normalized.includes('valor base')) return 'valorBase'
  if (normalized.includes('percentual') && normalized.includes('comissao')) return 'percentualComissao'
  if (normalized.includes('valor') && normalized.includes('comissao')) return 'valorComissao'
  if (normalized.includes('emissao')) return 'dataEmissao'
  if (normalized.includes('apropriacao') && normalized.includes('data')) return 'dataApropriacao'
  if (normalized.includes('premio bruto')) return 'valorPremioBruto'
  if (normalized.includes('premio tarifario')) return 'valorPremioTarifario'
  if (normalized.includes('movimento')) return 'dataMovimento'
  if (normalized.includes('agendamento')) return 'dataAgendamento'
  if (normalized.includes('pagamento efetivo')) return 'dataPagamentoEfetivo'
  if (normalized.includes('data') && normalized.includes('pagamento')) return 'dataPagamento'
  if (normalized.includes('status') && normalized.includes('recibo')) return 'statusRecibo'
  if (normalized.includes('cobranca')) return 'nrCobranca'
  return null
}

function buildColumnMap(headerRow: unknown[]): Map<FieldKey, number> {
  const map = new Map<FieldKey, number>()
  headerRow.forEach((cell, colIdx) => {
    const key = normalizeHeaderKey(cell)
    if (!key) return
    const field = mapHeaderToField(key)
    if (field && !map.has(field)) map.set(field, colIdx)
  })
  return map
}

function parseMoneyCell(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  let s = String(v).trim().replace(/\s/g, '').replace(/R\$\s?/i, '')
  if (!s) return null
  s = s.replace(/[^\d,.-]/g, '')
  const lastComma = s.lastIndexOf(',')
  const lastDot = s.lastIndexOf('.')
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      s = s.replace(/\./g, '').replace(',', '.')
    } else {
      s = s.replace(/,/g, '')
    }
  } else if (lastComma > -1) {
    s = s.replace(',', '.')
  }
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

function cellString(row: unknown[], col: number | undefined): string {
  if (col === undefined) return ''
  const v = row[col]
  if (v === null || v === undefined) return ''
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toLocaleDateString('pt-BR')
  }
  return String(v).trim().replace(/^'|'$/g, '')
}

function cellHasDisplayValue(v: unknown): boolean {
  if (v === null || v === undefined) return false
  if (v instanceof Date && !Number.isNaN(v.getTime())) return true
  if (typeof v === 'number' && !Number.isNaN(v)) return true
  return String(v).trim() !== ''
}

function rowHasAnyMappedColumnNonBlank(row: unknown[] | undefined, colMap: Map<FieldKey, number>): boolean {
  if (!row) return false
  for (const col of colMap.values()) {
    if (cellHasDisplayValue(row[col])) return true
  }
  return false
}

function matrixToRows(
  matrix: unknown[][],
  pasta: string,
  arquivo: string
): FinanceiroComissaoRow[] {
  if (!matrix.length) return []
  const headerRow = matrix[0] as unknown[]
  const colMap = buildColumnMap(headerRow)
  if (colMap.size === 0) return []

  const rows: FinanceiroComissaoRow[] = []
  for (let r = 1; r < matrix.length; r++) {
    const row = matrix[r] as unknown[]
    if (!rowHasAnyMappedColumnNonBlank(row, colMap)) continue

    const getStr = (key: FieldKey) => cellString(row, colMap.get(key))
    const getMoney = (key: FieldKey) => {
      const col = colMap.get(key)
      if (col === undefined) return null
      return parseMoneyCell(row[col])
    }

    rows.push({
      pasta,
      arquivo,
      codigoCorretor: getStr('codigoCorretor'),
      nomeCorretor: getStr('nomeCorretor'),
      cpfCnpj: getStr('cpfCnpj'),
      apolice: getStr('apolice'),
      numeroSub: getStr('numeroSub'),
      modulo: getStr('modulo'),
      estipulante: getStr('estipulante'),
      subEstipulante: getStr('subEstipulante'),
      fatura: getStr('fatura'),
      competencia: getStr('competencia'),
      valorApropriado: getMoney('valorApropriado'),
      tipoComissao: getStr('tipoComissao'),
      parcelaInicial: getStr('parcelaInicial'),
      numeroGrupoCobertura: getStr('numeroGrupoCobertura'),
      valorBase: getMoney('valorBase'),
      percentualComissao: getMoney('percentualComissao'),
      valorComissao: getMoney('valorComissao'),
      dataEmissao: getStr('dataEmissao'),
      dataApropriacao: getStr('dataApropriacao'),
      valorPremioBruto: getMoney('valorPremioBruto'),
      valorPremioTarifario: getMoney('valorPremioTarifario'),
      dataMovimento: getStr('dataMovimento'),
      dataAgendamento: getStr('dataAgendamento'),
      dataPagamento: getStr('dataPagamento'),
      dataPagamentoEfetivo: getStr('dataPagamentoEfetivo'),
      statusRecibo: getStr('statusRecibo'),
      nrCobranca: getStr('nrCobranca'),
    })
  }
  return rows
}

function detectCsvDelimiter(text: string): string {
  const firstLine = text.split(/\r?\n/).find((l) => l.trim()) ?? ''
  const semicolons = (firstLine.match(/;/g) || []).length
  const commas = (firstLine.match(/,/g) || []).length
  return semicolons >= commas ? ';' : ','
}

export type ParseComissaoFileResult =
  | { ok: true; rows: FinanceiroComissaoRow[] }
  | { ok: false; message: string }

/** Parse a partir de ArrayBuffer (usado no Web Worker e no fallback da thread principal). */
export function parseComissaoBuffer(
  buf: ArrayBuffer,
  fileName: string,
  pasta: string
): ParseComissaoFileResult {
  try {
    const lower = fileName.toLowerCase()

    if (lower.endsWith('.csv')) {
      const text = new TextDecoder('utf-8').decode(buf)
      const delimiter = detectCsvDelimiter(text)
      const workbook = XLSX.read(text, { type: 'string', FS: delimiter, raw: true })
      const sheetName = workbook.SheetNames[0]
      if (!sheetName) {
        return { ok: false, message: 'O CSV não contém dados.' }
      }
      const sheet = workbook.Sheets[sheetName]
      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: true,
      }) as unknown[][]
      return { ok: true, rows: matrixToRows(matrix, pasta, fileName) }
    }

    const workbook = XLSX.read(buf, { type: 'array', cellDates: true })
    if (!workbook.SheetNames?.length) {
      return { ok: false, message: 'O arquivo não contém nenhuma aba.' }
    }

    const allRows: FinanceiroComissaoRow[] = []
    for (const sheetName of workbook.SheetNames) {
      const sheet = workbook.Sheets[sheetName]
      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: true,
      }) as unknown[][]
      allRows.push(...matrixToRows(matrix, pasta, fileName))
    }
    return { ok: true, rows: allRows }
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Não foi possível ler o arquivo.',
    }
  }
}

export async function parseComissaoFile(
  file: File,
  pasta: string
): Promise<ParseComissaoFileResult> {
  try {
    const buf = await file.arrayBuffer()
    return parseComissaoBuffer(buf, file.name, pasta)
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Não foi possível ler o arquivo.',
    }
  }
}

export function formatMoneyBr(value: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
